import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getPagos, deletePago } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Trash2, BadgeDollarSign, FileText, Link2, ExternalLink, Pencil } from 'lucide-react';

const TIPO_COLORS = {
  EGRESO: 'bg-red-100 text-red-700',
  INGRESO: 'bg-emerald-100 text-emerald-700',
  TRANSFERENCIA: 'bg-blue-100 text-blue-700'
};

const PagosPage = () => {
  const navigate = useNavigate();
  const { empresaActual } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getPagos(empresaActual.id);
      setItems(res.data);
    } catch (error) {
      toast.error('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Manejar eliminar - verifica conciliación antes
  const handleDelete = async (item) => {
    if (item.conciliado) {
      toast.error(`No se puede eliminar. Este pago está conciliado. Primero desconcilia el movimiento bancario.`, { duration: 5000 });
      return;
    }
    if (!window.confirm('¿Eliminar este pago? Esto revertirá los saldos asociados.')) return;
    try {
      await deletePago(item.id);
      toast.success('Pago eliminado');
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al eliminar';
      toast.error(msg, { duration: 5000 });
    }
  };

  // Manejar editar - verifica conciliación antes
  const handleEdit = (item) => {
    if (item.conciliado) {
      toast.error(`No se puede editar. Este pago está conciliado. Primero desconcilia el movimiento bancario.`, { duration: 5000 });
      return;
    }
    // Navegar a editar según el tipo de documento vinculado
    if (item.compra_id_vinculada) {
      navigate(`/compras/${item.compra_id_vinculada}/editar?tipo=${item.tipo_compra_vinculada}`);
    } else if (item.venta_id_vinculada) {
      navigate('/ventas');
    } else {
      toast.info('Este pago no tiene un documento editable asociado');
    }
  };

  // Navegar al documento vinculado
  const handleNavigateToDoc = (item) => {
    if (item.compra_id_vinculada) {
      const tipo = item.tipo_compra_vinculada;
      navigate(`/compras/${item.compra_id_vinculada}/editar?tipo=${tipo}`);
    } else if (item.letra_id_vinculada) {
      navigate('/letras');
    } else if (item.planilla_id_vinculada) {
      navigate(`/nomina/planillas`);
    } else if (item.cxc_id_vinculada) {
      navigate('/creditos');
    } else if (item.venta_id_vinculada) {
      navigate('/ventas');
    }
  };

  // Obtener icono según tipo de documento
  const getDocIcon = (docText) => {
    if (!docText) return null;
    if (docText.includes('FAC:')) return <FileText className="w-3 h-3 text-blue-500" />;
    if (docText.includes('GASTO:')) return <FileText className="w-3 h-3 text-orange-500" />;
    if (docText.includes('LETRA:')) return <FileText className="w-3 h-3 text-purple-500" />;
    if (docText.includes('PLANILLA:')) return <FileText className="w-3 h-3 text-green-500" />;
    if (docText.includes('CREDITO:')) return <FileText className="w-3 h-3 text-amber-500" />;
    if (docText.includes('VENTA:')) return <FileText className="w-3 h-3 text-emerald-500" />;
    return <Link2 className="w-3 h-3" />;
  };

  // Formatear texto del documento para mostrar
  const formatDocText = (docText) => {
    if (!docText) return null;
    return docText
      .replace('FAC:', 'Factura ')
      .replace('GASTO:', 'Gasto ')
      .replace('LETRA:', 'Letra ')
      .replace('PLANILLA:', 'Planilla #')
      .replace('CREDITO:', 'Crédito #')
      .replace('VENTA:', 'Venta ');
  };

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  const totalEgresos = items.filter(i => i.tipo === 'EGRESO').reduce((sum, i) => sum + (i.monto_pen || 0), 0);
  const totalIngresos = items.filter(i => i.tipo === 'INGRESO').reduce((sum, i) => sum + (i.monto_pen || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BadgeDollarSign className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Movimientos / Pagos</h1>
            <div className="flex gap-4 text-sm">
              <span className="text-emerald-600">Ingresos: S/ {totalIngresos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
              <span className="text-red-600">Egresos: S/ {totalEgresos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay movimientos registrados</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>Medio</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead>Documento Vinculado</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.fecha}</TableCell>
                    <TableCell><Badge className={TIPO_COLORS[item.tipo]}>{item.tipo}</Badge></TableCell>
                    <TableCell>{item.cuenta_nombre}</TableCell>
                    <TableCell>{item.medio_pago || '-'}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={item.ref_pago || item.nota}>{item.ref_pago || item.nota || '-'}</TableCell>
                    <TableCell className="max-w-[200px]">
                      {item.documentos_vinculados ? (
                        <button
                          onClick={() => handleNavigateToDoc(item)}
                          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer group"
                          title={`Ir a: ${formatDocText(item.documentos_vinculados)}`}
                        >
                          {getDocIcon(item.documentos_vinculados)}
                          <span className="truncate max-w-[150px]">
                            {formatDocText(item.documentos_vinculados)}
                          </span>
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className={`text-right font-bold ${item.tipo === 'EGRESO' ? 'text-red-600' : 'text-emerald-600'}`}>
                      {item.tipo === 'EGRESO' ? '-' : '+'} {item.moneda_codigo} {item.monto_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-center">
                      {item.conciliado ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Conciliado
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">Pendiente</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEdit(item)}
                          title={item.conciliado ? "No se puede editar - desconcilia primero" : "Editar"}
                        >
                          <Pencil className={`w-4 h-4 ${item.conciliado ? 'text-gray-300' : 'text-blue-500'}`} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(item)}
                          title={item.conciliado ? "No se puede eliminar - desconcilia primero" : "Eliminar pago"}
                        >
                          <Trash2 className={`w-4 h-4 ${item.conciliado ? 'text-gray-300' : 'text-red-500'}`} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PagosPage;
